import { LabelValue } from '@safe-global/store/gateway/types'
import type { Transaction, QueuedItemPage } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import { useMemo } from 'react'
import { useAppSelector } from '@/store'
import { selectPendingTxIdsBySafe, selectPendingTxs } from '@/store/pendingTxsSlice'
import { selectQueuedTransactions } from '@/store/txQueueSlice'
import useAsync from '@safe-global/utils/hooks/useAsync'
import {
  isConflictHeaderQueuedItem,
  isLabelListItem,
  isMultisigExecutionInfo,
  isTransactionQueuedItem,
} from '@/utils/transaction-guards'
import useSafeInfo from './useSafeInfo'
import { shallowEqual } from 'react-redux'
import { getTransactionQueue } from '@/services/transactions'

export const usePendingTxIds = (): Array<Transaction['id']> => {
  const { safe, safeAddress } = useSafeInfo()
  const { chainId } = safe
  return useAppSelector((state) => selectPendingTxIdsBySafe(state, chainId, safeAddress), shallowEqual)
}

export const useHasPendingTxs = (): boolean => {
  const { safe, safeAddress } = useSafeInfo()
  const { chainId, nonce } = safe
  return useAppSelector((state) => {
    const pendingTxs = selectPendingTxs(state)
    return selectPendingTxIdsBySafe(state, chainId, safeAddress).some((id) => pendingTxs[id].nonce >= nonce)
  })
}

/**
 * Show unsigned pending queue only in 1/X Safes
 */
export const useShowUnsignedQueue = (): boolean => {
  const { safe } = useSafeInfo()
  const hasPending = useHasPendingTxs()
  return safe.threshold === 1 && hasPending
}

export const filterUntrustedQueue = (
  untrustedQueue: QueuedItemPage,
  pendingIds: Array<Transaction['id']>,
  trustedIds: Array<Transaction['id']> = [],
) => {
  // Only keep labels and pending unsigned transactions that the trusted queue does not already show
  const results = untrustedQueue.results
    .filter(
      (item) =>
        !isTransactionQueuedItem(item) ||
        (pendingIds.includes(item.transaction.id) && !trustedIds.includes(item.transaction.id)),
    )
    .filter((item) => !isConflictHeaderQueuedItem(item))
    .filter(
      (item) =>
        !isTransactionQueuedItem(item) ||
        (isTransactionQueuedItem(item) &&
          isMultisigExecutionInfo(item.transaction.executionInfo) &&
          item.transaction.executionInfo.confirmationsSubmitted === 0),
    )
  // Adjust the first label ("Next" -> "Pending") by creating a new object
  let adjustedResults = results
  if (results.length > 0 && isLabelListItem(results[0])) {
    adjustedResults = [...results]
    adjustedResults[0] = { ...results[0], label: 'Pending' as LabelValue }
  }

  const transactions = adjustedResults.filter((item) => isTransactionQueuedItem(item))

  return transactions.length ? { results: adjustedResults } : undefined
}

export function getNextTransactions(queue: QueuedItemPage): QueuedItemPage {
  const queueLabelIndex = queue.results.findIndex((item) => isLabelListItem(item) && item.label === LabelValue.Queued)
  const nextTransactions = queueLabelIndex === -1 ? queue.results : queue.results.slice(0, queueLabelIndex)
  return { results: nextTransactions }
}

export const usePendingTxsQueue = (): {
  page?: QueuedItemPage
  error?: string
  loading: boolean
} => {
  const { safe, safeAddress } = useSafeInfo()
  const { chainId } = safe
  const pendingIds = usePendingTxIds()
  const hasPending = pendingIds.length > 0
  const trustedQueue = useAppSelector(selectQueuedTransactions)
  const trustedIds = useMemo(() => trustedQueue?.map((item) => item.transaction.id) ?? [], [trustedQueue])

  const [untrustedNext, error, loading] = useAsync<QueuedItemPage | undefined>(
    async () => {
      if (!hasPending) return
      const untrustedQueue = await getTransactionQueue(chainId, safeAddress, { trusted: false })
      return getNextTransactions(untrustedQueue)
    },
    [chainId, safeAddress, hasPending],
    false,
  )

  const pendingTxPage = useMemo(() => {
    if (!untrustedNext || !pendingIds.length) return

    return filterUntrustedQueue(untrustedNext, pendingIds, trustedIds)
  }, [untrustedNext, pendingIds, trustedIds])

  return useMemo(
    () => ({
      page: pendingTxPage,
      error: error?.message,
      loading,
    }),
    [pendingTxPage, error, loading],
  )
}
